// App.js - WITH MODAL FUNCTIONALITY RESTORED AND GOOGLE ANALYTICS
import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from "react";
import io from "socket.io-client";
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import heatpump from './images/heat-pump.png';
import hellofresh from './images/dt-1-hello-fresh-co-uk.png';
import EnergyCertTrust from './images/energy-saving-certified.png';
import GreenBusinessCertified from './images/green-business-cert.png';
import EDFSmartMeter from './images/edf-smart-meter.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useUser,
} from "@clerk/clerk-react";
import "./App.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PayPalScriptProvider } from '@paypal/react-paypal-js';

// Import Material-UI components for Ask Jim
import { 
  Container, 
  Grid, 
  Typography, 
  TextField, 
  Button,  
  Box, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Collapse,
  Paper
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

// Register Chart.js components
Chart.register(...registerables);

// GOOGLE ANALYTICS INITIALIZATION
const initializeGoogleAnalytics = () => {
  // Your Google Analytics Measurement ID
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
  
  // Only initialize in production or if ID is provided
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    console.log('Google Analytics: Measurement ID not configured');
    return;
  }
  
  // Add Google Analytics script to head
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);
  
  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA_MEASUREMENT_ID}', {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title
    });
  `;
  document.head.appendChild(script2);
  
  console.log('Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
  return GA_MEASUREMENT_ID;
};

// GOOGLE ANALYTICS EVENT TRACKING FUNCTION
const trackGoogleAnalyticsEvent = (action, category, label, value = null) => {
  if (window.gtag) {
    window.gtag('event', action, {
      'event_category': category,
      'event_label': label,
      'value': value
    });
    console.log(`GA Event: ${category} - ${action} - ${label}`);
  }
};

// GOOGLE ANALYTICS PAGE VIEW TRACKING
const trackPageView = (path, title) => {
  if (window.gtag) {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX', {
      page_path: path,
      page_title: title
    });
    console.log(`GA Pageview: ${title} - ${path}`);
  }
};

// Dynamic Configuration
const CONFIG = {
  BACKEND_URL: process.env.REACT_APP_BACKEND_URL || 
    (process.env.NODE_ENV === "production" 
      ? "https://energy-meter-backend-s5im.onrender.com"
      : "http://localhost:3001"),
  
  DEFAULT_VISIBLE_SUPPLIERS: parseInt(process.env.REACT_APP_VISIBLE_SUPPLIERS) || 4,
  MAX_VISIBLE_SUPPLIERS: 10,
  
  SAMPLE_QUESTIONS: {
    supplier_comparison: [
      "Compare all energy suppliers and show me the best options",
      "Which supplier gives the best savings right now?",
      "Show me potential savings if I switch suppliers",
      "Analyze current supplier pricing and recommend the best one",
      "Compare Octopus Energy vs EDF Energy for my current usage"
    ],
    real_time: [
      "Analyze my current consumption patterns and suggest optimizations",
      "What's causing the high consumption during peak hours?",
      "Show me real-time energy usage trends and anomalies",
      "How can I optimize my energy usage based on current patterns?",
      "What are my current consumption rates and what do they mean?"
    ],
    general: [
      "Which supplier gives better savings for my usage?",
      "Compare current supplier prices and tariffs",
      "Analyze my energy consumption patterns and suggest improvements",
      "Recommend the best supplier based on my current usage",
      "What are the current trends in my energy consumption?"
    ]
  },
  
  DEFAULT_FALLBACK_SUPPLIERS: [
    { name: "Octopus Energy", costPerKwh: 29.8, tariff: "Flexible Octopus", contact: "0808 164 1088" },
    { name: "EDF Energy", costPerKwh: 32.8, tariff: "Blue Price Promise", contact: "0333 200 5100" },
    { name: "E.ON Next", costPerKwh: 31.2, tariff: "Next Fixed", contact: "0808 501 5200" },
    { name: "British Gas", costPerKwh: 34.5, tariff: "Standard Variable", contact: "0800 048 0202" },
    { name: "Scottish Power", costPerKwh: 33.1, tariff: "Standard", contact: "0800 027 0072" },
    { name: "OVO Energy", costPerKwh: 30.5, tariff: "Better Smart", contact: "0800 599 9440" },
    { name: "Utility Warehouse", costPerKwh: 28.9, tariff: "Multi-service", contact: "0333 777 8778" }
  ],
  
  // UPDATED: Configurable content for when NetZero form expands - USING IMAGE INSTEAD OF VIDEO
  NETZERO_EXPANSION_CONTENT: {
    leftColumn: {
      type: "image", // Changed from "video" to "image"
      // Using the heatpump image as a reliable static image
      source: heatpump,
      title: "Renewable Energy Solutions",
      description: "Exploring sustainable energy technologies for a greener future"
    },
    middleColumn: {
      type: "image",
      source: hellofresh,
      title: "Organic Food Partnerships",
      description: "Our commitment to 100% organic and sustainable food sources"
    }
  },
  
  // GmailApp Configuration
  GMAIL_APP_CONFIG: {
    apiEndpoint: process.env.REACT_APP_APPS_SCRIPT_URL || "YOUR_GOOGLE_APPS_SCRIPT_WEB_APP_URL",
    recipientEmail: "aadusarkodie1@gmail.com"
  },
  
  // NEW: Sustainability Journey Configuration
  SUSTAINABILITY_JOURNEY: {
    type: "video", // Can be "video" or "image"
    videoUrl: "https://goods-vod.kwcdn.com/goods-video/2e893b7d49267bbcf912d457f659eede65dad719.f30.mp4",
    fallbackImage: heatpump, // Fallback image if video fails to load
    title: "Our Sustainability Journey",
    description: "Watch our journey towards a greener future",
    autoplay: true,
    muted: true,
    loop: true,
    controls: true,
    showPoster: true,
    posterImage: heatpump // Optional poster image
  }
};

// Initialize meter data with affiliate links
const getDynamicMeterData = () => {
  // Affiliate configuration for energy suppliers
  const AFFILIATE_CONFIG = {
    "Octopus Energy": {
      baseUrl: "https://octopus.energy/",
      affiliateParam: "?awc=2334297_1_5d541736-1257-4e1a-a984-37e189817459",
      trackingId: "SPYDER-OCTOPUS",
      commissionRate: 0.05, // 5% commission
      contact: "0808 164 1088",
      defaultTariff: "Flexible Octopus",
      defaultCost: 23.28
    },
    "EDF Energy": {
      baseUrl: "https://www.edfenergy.com/",
      affiliateParam: "?awc=2334297_2_5d541736-1257-4e1a-a984-37e189817459",
      trackingId: "SPYDER-EDF",
      commissionRate: 0.04, // 4% commission
      contact: "0333 200 5100",
      defaultTariff: "Fixed",
      defaultCost: 23.28
    },
    "E.ON Next": {
      baseUrl: "https://www.eonnext.com/",
      affiliateParam: "?awc=2334297_3_5d541736-1257-4e1a-a984-37e189817459",
      trackingId: "SPYDER-EON",
      commissionRate: 0.03, // 3% commission
      contact: "0808 501 5200",
      defaultTariff: "Next Flex",
      defaultCost: 25.69
    }
  };

  // Function to generate affiliate links
  const generateAffiliateLink = (supplier, customParams = {}) => {
    const config = AFFILIATE_CONFIG[supplier];
    if (!config) return "#";
    
    const url = new URL(config.baseUrl);
    url.searchParams.set('affiliate_id', config.trackingId);
    url.searchParams.set('utm_source', 'SPYDER');
    url.searchParams.set('utm_medium', 'affiliate');
    url.searchParams.set('utm_campaign', 'energy_switch');
    
    // Add custom parameters if provided
    Object.entries(customParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
    
    return url.toString();
  };

  return {
    "SMR-98756-1-A": {
      supplier: "Octopus Energy",
      cost: AFFILIATE_CONFIG["Octopus Energy"].defaultCost,
      tariff: AFFILIATE_CONFIG["Octopus Energy"].defaultTariff,
      total: 0,
      contact: AFFILIATE_CONFIG["Octopus Energy"].contact,
      target: AFFILIATE_CONFIG["Octopus Energy"].baseUrl,
      affiliateLink: generateAffiliateLink("Octopus Energy", { tariff: "flexible" }),
      commissionRate: AFFILIATE_CONFIG["Octopus Energy"].commissionRate
    },
    "SMR-43563-2-A": {
      supplier: "EDF Energy",
      cost: AFFILIATE_CONFIG["EDF Energy"].defaultCost,
      tariff: AFFILIATE_CONFIG["EDF Energy"].defaultTariff,
      total: 0,
      contact: AFFILIATE_CONFIG["EDF Energy"].contact,
      target: AFFILIATE_CONFIG["EDF Energy"].baseUrl,
      affiliateLink: generateAffiliateLink("EDF Energy", { plan: "fixed" }),
      commissionRate: AFFILIATE_CONFIG["EDF Energy"].commissionRate
    },
    "SMR-65228-1-B": {
      supplier: "E.ON Next",
      cost: AFFILIATE_CONFIG["E.ON Next"].defaultCost,
      tariff: AFFILIATE_CONFIG["E.ON Next"].defaultTariff,
      total: 0,
      contact: AFFILIATE_CONFIG["E.ON Next"].contact,
      target: AFFILIATE_CONFIG["E.ON Next"].baseUrl,
      affiliateLink: generateAffiliateLink("E.ON Next", { plan: "nextflex" }),
      commissionRate: AFFILIATE_CONFIG["E.ON Next"].commissionRate
    }
  };
};

// Initialize all meters with default readings
const initializeMeterReadings = () => {
  const meterData = getDynamicMeterData();
  const initialReadings = {};
  
  Object.keys(meterData).forEach(meterId => {
    initialReadings[meterId] = {
      ...meterData[meterId],
      reading: "0.00",
      total: "0.00"
    };
  });
  
  return initialReadings;
};

// Function to track affiliate clicks
const trackAffiliateClick = (supplier, meterId) => {
  console.log(`Affiliate click tracked for ${supplier} from meter ${meterId}`);
  
  // Track in Google Analytics
  trackGoogleAnalyticsEvent('click', 'affiliate', `${supplier}_${meterId}`, 1);
  
  const clickData = {
    supplier,
    meterId,
    timestamp: new Date().toISOString(),
    userId: localStorage.getItem('userId') || 'anonymous'
  };
  
  localStorage.setItem(`lastAffiliateClick_${meterId}`, JSON.stringify(clickData));
};

// Helper function to generate fallback response with personalized data
const generateFallbackResponse = (currentSupplierData, readings, consumptionRates) => {
  // Find best supplier
  const suppliers = Object.values(currentSupplierData);
  const sortedSuppliers = [...suppliers].sort((a, b) => a.costPerKwh - b.costPerKwh);
  const bestSupplier = sortedSuppliers[0];
  
  // Calculate total consumption from current meters
  let totalConsumption = 0;
  let currentSuppliers = [];
  
  Object.entries(readings).forEach(([meterId, data]) => {
    if (data && data.reading) {
      totalConsumption += parseFloat(data.reading) || 0;
      if (data.supplier) {
        currentSuppliers.push(data.supplier);
      }
    }
  });
  
  // Get unique current suppliers
  const uniqueCurrentSuppliers = [...new Set(currentSuppliers)];
  
  // Get current consumption rates summary
  const consumptionSummary = Object.entries(consumptionRates).reduce((acc, [meterId, rate]) => {
    if (rate) {
      acc[rate] = (acc[rate] || 0) + 1;
    }
    return acc;
  }, {});
  
  // Calculate savings only if there's a better supplier available
  let savingsSection = '';
  let monthlySavings = 0;
  let bestSupplierName = "No suppliers available";
  let bestSupplierCost = "N/A";
  let bestSupplierTariff = "N/A";
  let bestSupplierContact = "";
  
  if (bestSupplier) {
    bestSupplierName = bestSupplier.supplier;
    bestSupplierCost = bestSupplier.costPerKwh;
    bestSupplierTariff = bestSupplier.tariff || "Standard";
    bestSupplierContact = bestSupplier.contact || "";
    
    if (sortedSuppliers.length > 1) {
      // Find the next best supplier that's different from the current best
      let nextBestSupplier = sortedSuppliers.find(s => s.costPerKwh > bestSupplier.costPerKwh);
      
      if (nextBestSupplier) {
        monthlySavings = (totalConsumption * (nextBestSupplier.costPerKwh - bestSupplier.costPerKwh) / 100 / 12);
        
        if (monthlySavings > 0) {
          savingsSection = `
            <div class="data-section">
              <h6>💰 Estimated Monthly Savings Potential:</h6>
              <p>
                Based on your current usage of <strong>${totalConsumption.toFixed(2)} kWh</strong>, switching to 
                <strong>${bestSupplierName}</strong> could save you approximately:
                <br/>
                <strong class="savings-amount">£${monthlySavings.toFixed(2)} per month</strong>
              </p>
            </div>
          `;
        }
      }
    }
  }
  
  // Generate HTML for fallback response
  return `
    <div class="fallback-response">
      <div class="service-limit-notice">
        <h4>⚠️ Service Temporarily Limited</h4>
        <p>Our advanced AI analysis service is currently experiencing high demand. While we work to restore full functionality, here's your personalized energy data:</p>
      </div>
      
      <div class="personalized-data">
        <h5>📊 Your Current Energy Profile</h5>
        
        <div class="data-section">
          <h6>Best Available Supplier for You:</h6>
          <div class="supplier-highlight">
            <strong>${bestSupplierName}</strong> - 
            ${bestSupplierCost}p/kWh
            <br/>
            <small>Tariff: ${bestSupplierTariff}</small>
            ${bestSupplierContact ? `<br/><small>Contact: ${bestSupplierContact}</small>` : ''}
          </div>
          ${bestSupplier ? `
          <p class="saving-tip">
            Based on current market rates, switching to this supplier could save you approximately 
            <strong>15-25%</strong> on your energy bills.
          </p>
          ` : '<p class="saving-tip">No supplier data available at the moment.</p>'}
        </div>
        
        <div class="data-section">
          <h6>Your Current Consumption:</h6>
          <ul>
            <li><strong>Total Consumption:</strong> ${totalConsumption.toFixed(2)} kWh</li>
            <li><strong>Current Supplier${uniqueCurrentSuppliers.length > 1 ? 's' : ''}:</strong> ${uniqueCurrentSuppliers.join(', ') || 'Not detected'}</li>
            ${Object.entries(consumptionSummary).map(([rate, count]) => 
              `<li><strong>${rate} Consumption:</strong> ${count} meter${count > 1 ? 's' : ''}</li>`
            ).join('')}
          </ul>
        </div>
        
        ${savingsSection}
        
        <div class="data-section">
          <h6>⚡ Quick Energy Tips:</h6>
          <ul>
            <li>Consider time-of-use tariffs if you have flexible usage patterns</li>
            <li>Check for government grants for home insulation improvements</li>
            <li>Monitor peak usage hours to identify savings opportunities</li>
            <li>Regularly compare tariffs as energy prices fluctuate</li>
          </ul>
        </div>
        
        <div class="fallback-footer">
          <p>
            <small>
              <em>Full AI analysis will resume shortly. In the meantime, you can:</em>
              <br/>
              • Try refreshing the page in a few minutes
              <br/>
              • Contact our support team for immediate assistance
              <br/>
              • Explore our detailed supplier comparison charts below
            </small>
          </p>
        </div>
      </div>
    </div>
    
    <style>
      .fallback-response {
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      }
      .service-limit-notice {
        background-color: #fff3cd;
        border-left: 4px solid #ffc107;
        padding: 15px;
        margin-bottom: 20px;
        border-radius: 4px;
      }
      .service-limit-notice h4 {
        color: #856404;
        margin-top: 0;
      }
      .personalized-data {
        background-color: #f8f9fa;
        padding: 20px;
        border-radius: 8px;
        border: 1px solid #e9ecef;
      }
      .data-section {
        margin-bottom: 25px;
        padding-bottom: 15px;
        border-bottom: 1px dashed #dee2e6;
      }
      .data-section:last-child {
        border-bottom: none;
      }
      .data-section h6 {
        color: #1976d2;
        margin-top: 0;
        margin-bottom: 10px;
        font-size: 16px;
      }
      .supplier-highlight {
        background-color: #e8f5e8;
        padding: 12px;
        border-radius: 6px;
        border-left: 4px solid #4caf50;
        margin: 10px 0;
      }
      .saving-tip {
        background-color: #e3f2fd;
        padding: 10px;
        border-radius: 4px;
        font-size: 14px;
      }
      .savings-amount {
        color: #4caf50;
        font-size: 18px;
        display: inline-block;
        margin-top: 5px;
        padding: 5px 10px;
        background-color: #f1f8e9;
        border-radius: 4px;
      }
      .fallback-footer {
        background-color: #e9ecef;
        padding: 15px;
        border-radius: 4px;
        margin-top: 20px;
        font-size: 14px;
      }
      .fallback-footer em {
        color: #6c757d;
      }
    </style>
  `;
};

// MODAL COMPONENT (RESTORED)
const Modal = memo(
  ({
    readings,
    onReadingChange,
    onDateChange,
    onAddReading,
    onRemoveReading,
    onClose,
    onSend,
    onPay,
    isSending,
    meterInfo,
    onAccountNumberChange,
  }) => {
    const calculateTotal = () => {
      return readings.reduce((sum, reading) => {
        const value = parseFloat(reading.value) || 0;
        return sum + (value * (meterInfo.cost / 100));
      }, 0).toFixed(2);
    };

    const handleAffiliateClick = () => {
      trackAffiliateClick(meterInfo.supplier, meterInfo.id);
      window.open(meterInfo.affiliateLink, "_blank");
    };

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h2>Meter Details - {meterInfo.id}</h2>
            </div>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="modal-body">
            <button 
              className="switch-supplier-button"
              onClick={handleAffiliateClick}
            >
              <a href={meterInfo.affiliateLink} target="_blank" rel="noopener noreferrer">
                Switch to {meterInfo.supplier}
              </a>
            </button>
{/*<small>We earn a {meterInfo.commissionRate * 100}% commission if you switch</small>*/}

            <div className="detail-row">
              <div className="account-number-input">
                <h3>Customer Email:</h3>
                <input
                  type="text"
                  id="accountNumber"
                  value={meterInfo.accountNumber}
                  onChange={onAccountNumberChange}
                  placeholder="Enter your email"
                  className="account-input"
                />
              </div>
              <h3><span className="updateReader">Send your reading(s)</span></h3>
              {readings.map((reading, index) => (
                <div key={index} className="reading-row">
                  <div className="reading-input-container">
                    <input
                      type="number"
                      value={reading.value}
                      onChange={(e) => onReadingChange(index, e.target.value)}
                      className="reading-input"
                      step="0.01"
                      min="0"
                      placeholder="Reading value"
                    />
                    <span className="reading-unit">kWh</span>
                  </div>
                  <div className="date-input-container">
                    <input
                      type="date"
                      value={reading.date}
                      onChange={(e) => onDateChange(index, e.target.value)}
                      className="date-input"
                    />
                  </div>
                  {index === readings.length - 1 ? (
                    <button 
                      type="button" 
                      className="add-reading-button"
                      onClick={onAddReading}
                    >
                      +
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      className="remove-reading-button"
                      onClick={() => onRemoveReading(index)}
                    >
                      -
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="detail-row">
              <h3>Supplier Information</h3>      
              <p>Supplier: <span className="energy-supplier">{meterInfo.supplier}</span></p>
              <p>Tariff Type: {meterInfo.tariff}</p>
            </div>
            <div className="detail-row">
              <h3>How much you will be paying</h3>
              <p>Rate per kWh: {meterInfo.cost}p</p>
              <p>Total Cost: £{calculateTotal()}</p>
            </div>
            <div className="detail-row">
              <button
                className="send-reading-button"
                onClick={onSend}
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Send Meter Reading(s) to SPYDER"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

Modal.displayName = "Modal";

// Optimized AskJim component - WITH INLINE COLLAPSIBLE FORM
const AskJim = memo(({ readings, consumptionRates }) => {
  const [question, setQuestion] = useState('');
  const [llmResponse, setLlmResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [similarQuestions, setSimilarQuestions] = useState([]);
  const [analysisType, setAnalysisType] = useState('general');
  const [expandedSuppliers, setExpandedSuppliers] = useState(false);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    handleGeneralQuestions: true,
    responsePolicy: 'mixed',
    visibleSuppliersCount: CONFIG.DEFAULT_VISIBLE_SUPPLIERS
  });
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  
  // NEW STATE FOR NETZERO FORM
  const [showNetZeroForm, setShowNetZeroForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Use ref to stabilize the config form
  const configFormRef = useRef(null);
  
  // NEW: Ref to track scroll position
  const scrollPositionRef = useRef(0);
  const formContainerRef = useRef(null);

  useEffect(() => {
    fetchDynamicSuppliers();
  }, []);

  const fetchDynamicSuppliers = async () => {
    try {
      const response = await fetch(`${CONFIG.BACKEND_URL}/api/config/suppliers`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSuppliers(data.suppliers || CONFIG.DEFAULT_FALLBACK_SUPPLIERS);
      }
    } catch (error) {
      console.log('Using default supplier data');
      setAvailableSuppliers(CONFIG.DEFAULT_FALLBACK_SUPPLIERS);
    }
  };

  // Optimized supplier data calculation - FIXED for stable rendering
  const currentSupplierData = useMemo(() => {
    const supplierMap = {};
    
    // Use stable references
    const readingsEntries = Object.entries(readings);
    
    readingsEntries.forEach(([meterId, data]) => {
      if (data && data.supplier) {
        const currentReading = parseFloat(data.reading) || 0;
        const costPerKwh = data.cost || 0;
        const totalCost = (currentReading * (costPerKwh / 100)).toFixed(2);
        
        supplierMap[data.supplier] = {
          supplier: data.supplier,
          costPerKwh: costPerKwh,
          currentReading: currentReading,
          totalCost: totalCost,
          tariff: data.tariff || "Standard",
          consumptionRate: consumptionRates[meterId] || "Normal",
          contact: data.contact || "Contact not available",
          isCurrentSupplier: true,
          meterId: meterId
        };
      }
    });
    
    // Use stable reference for available suppliers
    const suppliersList = [...availableSuppliers];
    
    suppliersList.forEach(supplier => {
      if (!supplierMap[supplier.name]) {
        supplierMap[supplier.name] = {
          ...supplier,
          isCurrentSupplier: false,
          currentReading: 0,
          totalCost: "0.00",
          consumptionRate: "Unknown"
        };
      }
    });
    
    return supplierMap;
  }, [
    // Stable dependencies
    JSON.stringify(Object.keys(readings).sort()),
    JSON.stringify(availableSuppliers.map(s => s.name).sort())
  ]);

  // UPDATED: handleAskJim with proper backend integration and price alert email
  const handleAskJim = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    setLlmResponse('');
    setSimilarQuestions([]);

    // Track Google Analytics event for AI query
    trackGoogleAnalyticsEvent('submit', 'ai_query', analysisType, 1);

    try {
      const response = await fetch(`${CONFIG.BACKEND_URL}/api/llm/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: question,
          maxRecords: 25,
          context: {
            analysisType: analysisType,
            config: aiConfig
          }
        }),
      });

      // Parse the response
      const result = await response.json();

      // Check if this is a rate limit fallback response from the backend
      if (result.answer && typeof result.answer === 'object' && 
          result.answer.fallback && result.answer.error === 'RATE_LIMIT_EXCEEDED') {
        // Backend detected a rate limit - show our fallback
        const fallbackResponse = generateFallbackResponse(currentSupplierData, readings, consumptionRates);
        setLlmResponse(fallbackResponse);
        toast.info('Service experiencing high demand. Showing personalized energy data instead.');
        
        // Track fallback usage
        trackGoogleAnalyticsEvent('fallback', 'ai_query', 'rate_limit', 1);
        
        setIsLoading(false);
        return;
      }

      // Check for other errors in the response
      if (result.error || !result.answer) {
        const fallbackResponse = generateFallbackResponse(currentSupplierData, readings, consumptionRates);
        setLlmResponse(fallbackResponse);
        toast.info('Service issue detected. Showing personalized energy data.');
        
        // Track error
        trackGoogleAnalyticsEvent('error', 'ai_query', 'service_issue', 1);
        
        setIsLoading(false);
        return;
      }

      // Successful response
      setLlmResponse(result.answer);
      
      if (result.similarQuestions && result.similarQuestions.length > 0) {
        setSimilarQuestions(result.similarQuestions);
      }
      
      toast.success(`Analysis complete using ${analysisType.replace('_', ' ')}`);
      
      // Track successful AI response
      trackGoogleAnalyticsEvent('success', 'ai_query', analysisType, result.answer.length);
      
    } catch (error) {
      console.error('Error calling LLM service:', error);
      
      // Network errors or other exceptions
      const fallbackResponse = generateFallbackResponse(currentSupplierData, readings, consumptionRates);
      setLlmResponse(fallbackResponse);
      toast.info('Service connection issue. Showing personalized energy data.');
      
      // Track error
      trackGoogleAnalyticsEvent('error', 'ai_query', 'network_error', 1);
    } finally {
      setIsLoading(false);
    }
  };

  // Configuration form handlers - NO FLICKERING
  const handleToggleConfigForm = () => {
    setShowConfigForm(!showConfigForm);
    trackGoogleAnalyticsEvent('toggle', 'ui', 'config_form', showConfigForm ? 0 : 1);
  };

  const handleToggleGeneralQuestions = () => {
    setAiConfig(prev => ({
      ...prev,
      handleGeneralQuestions: !prev.handleGeneralQuestions
    }));
    trackGoogleAnalyticsEvent('toggle', 'ai_config', 'general_questions', aiConfig.handleGeneralQuestions ? 0 : 1);
  };

  const handleResponsePolicyChange = (policy) => {
    setAiConfig(prev => ({
      ...prev,
      responsePolicy: policy
    }));
    trackGoogleAnalyticsEvent('change', 'ai_config', 'response_policy', 1);
  };

  const handleIncreaseSuppliers = () => {
    if (aiConfig.visibleSuppliersCount < CONFIG.MAX_VISIBLE_SUPPLIERS) {
      setAiConfig(prev => ({
        ...prev,
        visibleSuppliersCount: prev.visibleSuppliersCount + 1
      }));
      trackGoogleAnalyticsEvent('increase', 'ui', 'visible_suppliers', aiConfig.visibleSuppliersCount + 1);
    }
  };

  const handleDecreaseSuppliers = () => {
    if (aiConfig.visibleSuppliersCount > 2) {
      setAiConfig(prev => ({
        ...prev,
        visibleSuppliersCount: prev.visibleSuppliersCount - 1
      }));
      trackGoogleAnalyticsEvent('decrease', 'ui', 'visible_suppliers', aiConfig.visibleSuppliersCount - 1);
    }
  };

  const handleSaveConfiguration = () => {
    toast.success('Configuration updated successfully!');
    setShowConfigForm(false);
    trackGoogleAnalyticsEvent('save', 'ai_config', 'configuration', 1);
  };

  const handleCancelConfiguration = () => {
    setShowConfigForm(false);
    trackGoogleAnalyticsEvent('cancel', 'ai_config', 'configuration', 0);
  };

  // FIXED: Modified handleSetPriceAlert to preserve scroll position
  const handleSetPriceAlert = () => {
    // Save current scroll position before opening form
    if (!showNetZeroForm) {
      scrollPositionRef.current = window.scrollY;
    }
    
    setShowNetZeroForm(!showNetZeroForm);
    trackGoogleAnalyticsEvent('toggle', 'price_alert', 'form', showNetZeroForm ? 0 : 1);
  };

  // FIXED: Proper form change handler
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // UPDATED: Proper form submission using the same Google Apps Script endpoint as Meter Grid
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in all required fields');
      trackGoogleAnalyticsEvent('validation_error', 'price_alert', 'form', 0);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Use the SAME Google Apps Script endpoint as the Meter Grid
      const response = await fetch(CONFIG.GMAIL_APP_CONFIG.apiEndpoint, {
        method: "POST",
        redirect: "follow",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify({
          userEmail: formData.email, // Use form email as recipient
          priceAlertData: {
            name: formData.name,
            email: formData.email,
            recipientEmail: "aadusarkodie1@gmail.com",
            subject: "Price Alert Subscription Request",
            message: `New price alert subscription request from ${formData.name} (${formData.email})`,
            timestamp: new Date().toISOString()
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Price alert request submitted successfully!');
        
        // Reset form
        setFormData({
          name: '',
          email: ''
        });
        setIsSubmitted(true);
        
        // Track successful submission
        trackGoogleAnalyticsEvent('submit', 'price_alert', 'success', 1);
        
        // Auto-hide form after 3 seconds
        setTimeout(() => {
          setShowNetZeroForm(false);
          setIsSubmitted(false);
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to submit price alert');
      }
      
    } catch (error) {
      console.error('Error submitting price alert:', error);
      toast.error('Failed to submit price alert. Please try again.');
      
      // Track error
      trackGoogleAnalyticsEvent('error', 'price_alert', 'submission_failed', 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelForm = () => {
    setShowNetZeroForm(false);
    setFormData({
      name: '',
      email: ''
    });
    setIsSubmitted(false);
    trackGoogleAnalyticsEvent('cancel', 'price_alert', 'form', 0);
  };

  // INLINE CONFIGURATION FORM COMPONENT
  const ConfigForm = memo(() => (
    <Collapse in={showConfigForm}>
      <Paper 
        ref={configFormRef}
        sx={{ 
          mt: 2, 
          mb: 2, 
          p: 3, 
          backgroundColor: '#f9f9f9',
          border: '1px solid #ddd',
          borderRadius: 2,
          position: 'relative'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
            <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            JIM Configuration
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleCancelConfiguration}
            sx={{ color: '#666' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={aiConfig.handleGeneralQuestions}
                onChange={handleToggleGeneralQuestions}
                color="primary"
              />
            }
            label="Handle general questions"
            sx={{ fontWeight: 'bold', color: '#333' }}
          />
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
            Response Policy:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {[
              { value: 'redirect', label: 'Redirect', description: 'Politely redirect to energy topics' },
              { value: 'answer', label: 'Answer', description: 'Try to answer general questions' },
              { value: 'mixed', label: 'Mixed', description: 'Brief answer then redirect' }
            ].map((policy) => (
              <Chip
                key={policy.value}
                label={policy.label}
                onClick={() => handleResponsePolicyChange(policy.value)}
                color={aiConfig.responsePolicy === policy.value ? 'primary' : 'default'}
                variant={aiConfig.responsePolicy === policy.value ? 'filled' : 'outlined'}
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        </Box>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
            Visible Suppliers:
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            p: 2,
            backgroundColor: 'white',
            borderRadius: 1,
            border: '1px solid #ddd',
            maxWidth: '300px'
          }}>
            <IconButton 
              size="small" 
              onClick={handleDecreaseSuppliers}
              disabled={aiConfig.visibleSuppliersCount <= 2}
              sx={{
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              <RemoveIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 'bold', minWidth: '100px', textAlign: 'center' }}>
              {aiConfig.visibleSuppliersCount} suppliers
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleIncreaseSuppliers}
              disabled={aiConfig.visibleSuppliersCount >= CONFIG.MAX_VISIBLE_SUPPLIERS}
              sx={{
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            onClick={handleCancelConfiguration}
            variant="outlined"
            sx={{
              color: '#666',
              borderColor: '#ddd',
              '&:hover': {
                borderColor: '#999',
                backgroundColor: '#f0f0f0'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveConfiguration}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#1976d2',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            Save Configuration
          </Button>
        </Box>
      </Paper>
    </Collapse>
  ));

  ConfigForm.displayName = "ConfigForm";

  // FIXED CurrentSupplierData - WITH INLINE CONFIG FORM
  const CurrentSupplierData = memo(() => {
    const suppliers = Object.values(currentSupplierData);
    
    // Stabilize the sorting - only re-sort when supplier list changes
    const allSuppliers = useMemo(() => {
      return [...suppliers].sort((a, b) => a.costPerKwh - b.costPerKwh);
    }, [suppliers.length]); // Only depend on count, not values
    
    const bestSupplier = allSuppliers[0];
    const visibleCount = expandedSuppliers ? allSuppliers.length : aiConfig.visibleSuppliersCount;
    
    // Memoize visible suppliers to prevent re-renders
    const visibleSuppliers = useMemo(() => 
      allSuppliers.slice(0, visibleCount), 
      [allSuppliers, visibleCount]
    );
    
    const hiddenCount = Math.max(0, allSuppliers.length - aiConfig.visibleSuppliersCount);

    if (Object.keys(currentSupplierData).length === 0) return null;

    return (
      <>
        <Card sx={{ mt: 2, mb: 2, bgcolor: 'success.light', color: 'white' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">
                📊 Real-time Supplier Data
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption">
                  {allSuppliers.length} suppliers available
                </Typography>
                <Tooltip title="Configure display settings">
                  <IconButton 
                    size="small" 
                    onClick={handleToggleConfigForm}
                    sx={{ color: 'white' }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {bestSupplier && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Best option:</strong> {bestSupplier.supplier} at {bestSupplier.costPerKwh}p/kWh
                {bestSupplier.isCurrentSupplier && ' (Your current supplier)'}
              </Typography>
            )}
            
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.5,
              mb: 2
            }}>
              {visibleSuppliers.map((supplier, index) => (
                <Card 
                  key={`${supplier.supplier}-${index}`}
                  sx={{ 
                    backgroundColor: supplier.supplier === bestSupplier?.supplier ? '#ffd700' : 
                                   supplier.isCurrentSupplier ? '#4caf50' : 'white',
                    color: supplier.supplier === bestSupplier?.supplier ? '#000' : 
                          supplier.isCurrentSupplier ? 'white' : '#2e7d32',
                    p: 1.5,
                    position: 'relative',
                    border: supplier.supplier === bestSupplier?.supplier ? '2px solid #ffd700' : 
                           supplier.isCurrentSupplier ? '2px solid #4caf50' : '1px solid #ccc'
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold', 
                      mb: 0.5,
                      fontSize: '0.85rem'
                    }}>
                      {supplier.supplier}
                    </Typography>
                    <Box>
                      {supplier.supplier === bestSupplier?.supplier && (
                        <Chip label="👑 BEST" size="small" sx={{ height: 20, fontSize: '0.6rem', mb: 0.5 }} />
                      )}
                      {supplier.isCurrentSupplier && (
                        <Chip label="🏠 YOURS" size="small" sx={{ height: 20, fontSize: '0.6rem' }} />
                      )}
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 0.5 }}>
                    {supplier.costPerKwh}p/kWh
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    {supplier.tariff}
                  </Typography>
                  {supplier.contact && (
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                      📞 {supplier.contact}
                    </Typography>
                  )}
                </Card>
              ))}
            </Box>
            
            {hiddenCount > 0 && !expandedSuppliers && (
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setExpandedSuppliers(true)}
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': { 
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderColor: 'white'
                    }
                  }}
                  startIcon={<ExpandMoreIcon />}
                >
                  Show +{hiddenCount} more supplier{hiddenCount !== 1 ? 's' : ''}
                </Button>
              </Box>
            )}
            
            {expandedSuppliers && allSuppliers.length > aiConfig.visibleSuppliersCount && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setExpandedSuppliers(false)}
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': { 
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderColor: 'white'
                    }
                  }}
                  startIcon={<ExpandLessIcon />}
                >
                  Show fewer suppliers
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
        
        {/* INLINE CONFIGURATION FORM */}
        <ConfigForm />
      </>
    );
  });

  CurrentSupplierData.displayName = "CurrentSupplierData";

  const AnalysisTypeSelector = memo(() => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Analysis Mode:
      </Typography>
      <Card className="analysis-mode-card" sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { value: 'general', label: 'General Analysis', icon: '🤖' },
            { value: 'real_time', label: 'Real-time Data', icon: '⚡' },
            { value: 'supplier_comparison', label: 'Supplier Comparison', icon: '🏆' }
          ].map((type) => (
            <Chip
              key={type.value}
              icon={<span>{type.icon}</span>}
              label={type.label}
              onClick={() => {
                setAnalysisType(type.value);
                trackGoogleAnalyticsEvent('change', 'analysis_type', type.value, 1);
              }}
              color={analysisType === type.value ? 'primary' : 'default'}
              variant={analysisType === type.value ? 'filled' : 'outlined'}
              clickable
              sx={{ mb: 0.5 }}
            />
          ))}
        </Box>
      </Card>
    </Box>
  ));

  AnalysisTypeSelector.displayName = "AnalysisTypeSelector";

  // UPDATED: Image Component for left column (was VideoContent)
  const LeftColumnContent = memo(() => {
    if (!showNetZeroForm) return null;

    const { leftColumn } = CONFIG.NETZERO_EXPANSION_CONTENT;
    
    if (leftColumn.type === "image") {
      return (
        <Box sx={{ mt: 2 }}>
          <Card sx={{ bgcolor: '#f0f7ff', border: '1px solid #d1e3ff' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {leftColumn.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                {leftColumn.description}
              </Typography>
              <Box sx={{ 
                height: '200px',
                overflow: 'hidden',
                borderRadius: 1
              }}>
                <img 
                  src={leftColumn.source}
                  alt={leftColumn.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  loading="lazy"
                  onLoad={() => trackGoogleAnalyticsEvent('load', 'image', leftColumn.title, 1)}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }
    
    // Fallback to video if type is not image (though config is now set to image)
    return (
      <Box sx={{ mt: 2 }}>
        <Card sx={{ bgcolor: '#f0f7ff', border: '1px solid #d1e3ff' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              {leftColumn.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              {leftColumn.description}
            </Typography>
            <Box sx={{ 
              position: 'relative', 
              paddingTop: '56.25%', // 16:9 aspect ratio
              overflow: 'hidden',
              borderRadius: 1,
              backgroundColor: '#000'
            }}>
              <iframe
                src={leftColumn.source}
                title={leftColumn.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                onLoad={() => trackGoogleAnalyticsEvent('load', 'video', leftColumn.title, 1)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  });

  LeftColumnContent.displayName = "LeftColumnContent";

  // Image Component for middle column (unchanged)
  const MiddleColumnContent = memo(() => {
    if (!showNetZeroForm) return null;

    const { middleColumn } = CONFIG.NETZERO_EXPANSION_CONTENT;
    
    return (
      <Box sx={{ mt: 2 }}>
        <Card sx={{ bgcolor: '#f0f7ff', border: '1px solid #d1e3ff' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
              {middleColumn.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              {middleColumn.description}
            </Typography>
            <Box sx={{ 
              height: '200px',
              overflow: 'hidden',
              borderRadius: 1
            }}>
              <img 
                src={middleColumn.source}
                alt={middleColumn.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                loading="lazy"
                onLoad={() => trackGoogleAnalyticsEvent('load', 'image', middleColumn.title, 1)}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  });

  MiddleColumnContent.displayName = "MiddleColumnContent";

  // FIXED: NetZero Form Component with proper scroll handling
  const NetZeroForm = memo(() => {
    // Create refs for the input fields
    const nameInputRef = useRef(null);
    const emailInputRef = useRef(null);
    
    // Effect to restore scroll position when form is opened
    useEffect(() => {
      if (showNetZeroForm) {
        // Store the current scroll position
        const savedPosition = window.scrollY;
        
        // Focus on the first input with a small delay
        const timer = setTimeout(() => {
          if (nameInputRef.current) {
            nameInputRef.current.focus();
          }
          // Restore scroll position after focus
          window.scrollTo(0, savedPosition);
        }, 50);
        
        return () => clearTimeout(timer);
      }
    }, [showNetZeroForm]);

    if (!showNetZeroForm) return null;

    if (isSubmitted) {
      return (
        <Box sx={{ 
          mt: 2, 
          p: 3, 
          backgroundColor: '#e8f5e9', 
          borderRadius: 2,
          border: '1px solid #c8e6c9'
        }}>
          <Typography variant="h6" sx={{ color: '#1b5e20', mb: 1 }}>
            ✅ Successfully Submitted!
          </Typography>
          <Typography variant="body2" sx={{ color: '#1b5e20' }}>
            Your price alert request has been submitted. You'll receive a confirmation email shortly.
          </Typography>
        </Box>
      );
    }

    return (
      <Collapse 
        in={showNetZeroForm}
        onEntered={() => {
          // Ensure we don't scroll to the form when it opens
          if (scrollPositionRef.current > 0) {
            window.scrollTo(0, scrollPositionRef.current);
          }
        }}
      >
        <Paper 
          ref={formContainerRef}
          sx={{ 
            mt: 2, 
            p: 3, 
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: 2,
            // Prevent the form from causing layout shifts that affect scrolling
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
            📧 Set Up Price Alert
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            Fill in your details to receive price alerts for eco-friendly flights.
            Form will be submitted via backend email service.
          </Typography>
          
          <form onSubmit={handleFormSubmit}>
            <TextField
              fullWidth
              label="Name *"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              required
              sx={{ mb: 2 }}
              variant="outlined"
              size="small"
              disabled={isSubmitting}
              autoComplete="name"
              inputRef={nameInputRef}
              inputProps={{
                tabIndex: 1
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (emailInputRef.current) {
                    emailInputRef.current.focus();
                  }
                }
              }}
            />
            
            <TextField
              fullWidth
              label="Email *"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              required
              sx={{ mb: 3 }}
              variant="outlined"
              size="small"
              disabled={isSubmitting}
              autoComplete="email"
              inputRef={emailInputRef}
              inputProps={{
                tabIndex: 2
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!isSubmitting) {
                    handleFormSubmit(e);
                  }
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button 
                onClick={handleCancelForm}
                variant="outlined"
                size="small"
                disabled={isSubmitting}
                sx={{
                  color: '#666',
                  borderColor: '#ddd',
                  '&:hover': {
                    borderColor: '#999',
                    backgroundColor: '#f0f0f0'
                  }
                }}
                tabIndex={4}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                variant="contained"
                size="small"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: '#1b5e20',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#0d47a1'
                  }
                }}
                tabIndex={3}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Alert Request'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Collapse>
    );
  });

  NetZeroForm.displayName = "NetZeroForm";

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 4, 
        mb: 4,
        // Ensure the container doesn't prevent scrolling
        overflow: 'visible'
      }}
    >
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Ask <span style={{ color: '#1976d2', fontWeight: 'bold' }}>JIM</span> - Energy Analytics AI 
          </Typography>

          <AnalysisTypeSelector />
          <CurrentSupplierData />

          {/* FIXED: Three horizontally aligned grids in ONE ROW using Flexbox */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 2,
            width: '100%',
            // Ensure the flex container doesn't interfere with scrolling
            overflow: 'visible'
          }}>
            {/* COLUMN 1: Question Input */}
            <Box sx={{ 
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    💬 Your Question
                  </Typography>
                  <TextField
                    fullWidth
                    label={
                      analysisType === 'supplier_comparison' 
                        ? "Ask about supplier comparisons..."
                        : analysisType === 'real_time'
                        ? "Ask about current consumption..."
                        : "Ask JIM about energy optimization..."
                    }
                    variant="outlined"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isLoading) {
                        handleAskJim();
                        trackGoogleAnalyticsEvent('enter_key', 'ui', 'ai_query_submit', 1);
                      }
                    }}
                    disabled={isLoading}
                    multiline
                    minRows={3}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAskJim}
                    disabled={isLoading || !question.trim()}
                    fullWidth
                    size="large"
                    sx={{
                      height: '48px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
                      boxShadow: '0 2px 4px 1px rgba(33, 203, 243, .3)',
                      mb: 2
                    }}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isLoading ? 'Analyzing...' : 'Ask JIM'}
                  </Button>
                  
                  {/* NEW: Energy Certificate Trust Image under Ask JIM button */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mt: 1,
                    mb: 2
                  }}>
                    <img 
                      src={EnergyCertTrust}
                      alt="Energy Saving Certified"
                      style={{
                        width: '180px',
                        height: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                      loading="lazy"
                      onLoad={() => trackGoogleAnalyticsEvent('load', 'image', 'energy_certificate', 1)}
                    />
                  </Box>
                  
                  {/* UPDATED: Left column content (now image instead of video) */}
                  {showNetZeroForm && <LeftColumnContent />}
                </CardContent>
              </Card>
            </Box>

            {/* COLUMN 2: Sample Questions - OPTIMIZED */}
            <Box sx={{ 
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Card sx={{ bgcolor: 'grey.50', height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mb: 2 }}>
                    💡 Try these sample questions:
                  </Typography>
                  
                  {/* NEW: OPTIMIZED SAMPLE QUESTIONS DISPLAY */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 1,
                      maxHeight: { xs: '300px', md: '350px' },
                      overflowY: 'auto',
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(0,0,0,0.05)',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0,0,0,0.1)',
                        borderRadius: '3px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    {CONFIG.SAMPLE_QUESTIONS[analysisType]?.map((sample, index) => (
                      <Paper
                        key={index}
                        onClick={() => {
                          setQuestion(sample);
                          setTimeout(handleAskJim, 100);
                          trackGoogleAnalyticsEvent('click', 'sample_question', analysisType, index + 1);
                        }}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                            transform: 'translateY(-2px)',
                            boxShadow: 1
                          },
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          maxWidth: '100%',
                        }}
                        elevation={0}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            color: 'text.primary'
                          }}
                        >
                          {sample}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                  
                  {/* Display number of sample questions */}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      textAlign: 'center', 
                      mt: 1, 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}
                  >
                    {CONFIG.SAMPLE_QUESTIONS[analysisType]?.length || 0} sample questions available
                  </Typography>
                  
                  {/* Middle column content */}
                  {showNetZeroForm && <MiddleColumnContent />}
                </CardContent>
              </Card>
            </Box>

            {/* COLUMN 3: NetZero Initiatives */}
            <Box sx={{ 
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Card sx={{ bgcolor: 'grey.50', height: '100%' }}>
                <CardContent>
                  {/* UPDATED: Added green status indicator with darker green and positioned on the RIGHT */}
                  <Typography variant="subtitle2" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    mb: 2,
                    color: '#1b5e20',
                    position: 'relative',
                    paddingRight: '24px'
                  }}>
                    🌱 <h3>NetZero Initiatives</h3>
                    <div className="status-indicator" style={{ 
                      position: 'absolute', 
                      right: 0, 
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '18px',
                      height: '18px'
                    }}>
                      <span className="ping active" style={{ 
                        width: '18px',
                        height: '18px',
                        border: '2px solid #1b5e20'
                      }}></span>
                      <span className="dot active" style={{ 
                        width: '10px',
                        height: '10px',
                        backgroundColor: '#1b5e20'
                      }}></span>
                    </div>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: 'text.primary',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    mb: 2
                  }}>
                    <strong>Can I Book Flights that emit less CO₂?</strong>
                    <br />
                    Yes! If you set up a Price Alert, we'll watch plane ticket prices for you and notify you via email or push notification when prices change.
                  </Typography>
                  
                  <Box sx={{ mt: 2, mb: 2 }}>
                    {/* UPDATED: Added onClick handler to reveal form */}
                    <Button 
                      variant="contained" 
                      size="small"
                      onClick={handleSetPriceAlert}
                      sx={{
                        backgroundColor: showNetZeroForm ? '#0d47a1' : '#1b5e20',
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                        px: 2,
                        py: 1,
                        '&:hover': {
                          backgroundColor: '#0d47a1',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 8px rgba(27, 94, 32, 0.3)'
                        }
                      }}
                    >
                      {showNetZeroForm ? '✖ Close Form' : '✨ Set Price Alert'}
                    </Button>
                  </Box>
                  
                  {/* NEW: Green Business Certified Image under Set Price Alert button */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mt: 2,
                    mb: 2
                  }}>
                    <img 
                      src={GreenBusinessCertified}
                      alt="Green Business Certified"
                      style={{
                        width: '180px',
                        height: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        marginTop:'25px'
                      }}
                      loading="lazy"
                      onLoad={() => trackGoogleAnalyticsEvent('load', 'image', 'green_business_certified', 1)}
                    />
                  </Box>
                  
                  {/* ADDED: NetZero Form Component */}
                  <NetZeroForm />
                </CardContent>
              </Card>
            </Box>
          </Box>

          {llmResponse && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                {analysisType === 'supplier_comparison' ? '🏆' : 
                 analysisType === 'real_time' ? '⚡' : '🤖'} JIM's Analysis
              </Typography>
              <Box 
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  bgcolor: 'background.paper',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: llmResponse }} />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});

AskJim.displayName = "AskJim";

// NEW: Configurable Sustainability Journey Component
const SustainabilityJourney = memo(() => {
  const { SUSTAINABILITY_JOURNEY } = CONFIG;
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  // Handle video play on load
  useEffect(() => {
    if (videoRef.current && SUSTAINABILITY_JOURNEY.autoplay) {
      const playVideo = async () => {
        try {
          videoRef.current.muted = SUSTAINABILITY_JOURNEY.muted;
          await videoRef.current.play();
          trackGoogleAnalyticsEvent('play', 'video', 'sustainability_journey', 1);
        } catch (error) {
          console.log('Autoplay prevented, showing controls instead');
          if (videoRef.current) {
            videoRef.current.controls = true;
          }
          trackGoogleAnalyticsEvent('autoplay_blocked', 'video', 'sustainability_journey', 0);
        }
      };
      playVideo();
    }
  }, []);

  const handleVideoError = () => {
    setVideoError(true);
    console.error('Video failed to load, showing fallback image');
    trackGoogleAnalyticsEvent('error', 'video', 'load_failed', 0);
  };

  return (
    <div className="sustainability-journey">
      <div className="sustainability-media-container">
        {SUSTAINABILITY_JOURNEY.type === "video" && !videoError ? (
          <div className="video-wrapper">
            <video
              ref={videoRef}
              width="100%"
              height="100%"
              autoPlay={SUSTAINABILITY_JOURNEY.autoplay}
              muted={SUSTAINABILITY_JOURNEY.muted}
              loop={SUSTAINABILITY_JOURNEY.loop}
              controls={SUSTAINABILITY_JOURNEY.controls}
              poster={SUSTAINABILITY_JOURNEY.showPoster ? SUSTAINABILITY_JOURNEY.posterImage : undefined}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
              onError={handleVideoError}
              onClick={() => trackGoogleAnalyticsEvent('click', 'video', 'sustainability_journey', 1)}
            >
              <source src={SUSTAINABILITY_JOURNEY.videoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {SUSTAINABILITY_JOURNEY.autoplay && !SUSTAINABILITY_JOURNEY.controls && (
              <div className="video-overlay">
                <button 
                  className="video-control-btn"
                  onClick={() => {
                    if (videoRef.current) {
                      videoRef.current.controls = true;
                    }
                    trackGoogleAnalyticsEvent('click', 'video_controls', 'show', 1);
                  }}
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Show Controls
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="fallback-image">
            <img 
              src={SUSTAINABILITY_JOURNEY.fallbackImage}
              alt={SUSTAINABILITY_JOURNEY.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
              loading="lazy"
              onLoad={() => trackGoogleAnalyticsEvent('load', 'image', 'sustainability_fallback', 1)}
            />
          </div>
        )}
      </div>
      <div className="sustainability-info">
        <h4>{SUSTAINABILITY_JOURNEY.title}</h4>
        <p>{SUSTAINABILITY_JOURNEY.description}</p>
        {SUSTAINABILITY_JOURNEY.type === "video" && !videoError && (
          <div className="video-stats">
            <small>Video: MP4 • Auto-play: {SUSTAINABILITY_JOURNEY.autoplay ? 'On' : 'Off'} • Loop: {SUSTAINABILITY_JOURNEY.loop ? 'On' : 'Off'}</small>
          </div>
        )}
      </div>
    </div>
    
  );
});

SustainabilityJourney.displayName = "SustainabilityJourney";

// Rest of the App component with MODAL FUNCTIONALITY
function App() {
  const [readings, setReadings] = useState(initializeMeterReadings());
  const [historicalData, setHistoricalData] = useState({});
  const [consumptionRates, setConsumptionRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [isReadingActive, setIsReadingActive] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [meterReadings, setMeterReadings] = useState([{ value: "", date: new Date().toISOString().split('T')[0] }]);
  const [calculatedResults, setCalculatedResults] = useState([]);
  const [userReading, setUserReading] = useState("");
  const { user } = useUser();
  const location = useLocation();

  // Use ref to store socket
  const socketRef = useRef(null);

  // Initialize Google Analytics on component mount
  useEffect(() => {
    // Initialize Google Analytics
    initializeGoogleAnalytics();
    
    // Track page view
    trackPageView(location.pathname, document.title);
    
    // Track app load
    trackGoogleAnalyticsEvent('load', 'app', 'initial_load', 1);
  }, [location]);

  useEffect(() => {
    console.log('Initial readings:', Object.keys(readings));
    console.log('Backend URL:', CONFIG.BACKEND_URL);
    
    // Initialize socket
    socketRef.current = io(CONFIG.BACKEND_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socketRef.current.on("connect", () => {
      console.log('Socket connected to:', CONFIG.BACKEND_URL);
      trackGoogleAnalyticsEvent('connect', 'socket', 'backend', 1);
    });

    socketRef.current.on("connect_error", (error) => {
      console.error('Socket connection error:', error);
      trackGoogleAnalyticsEvent('error', 'socket', 'connection_failed', 0);
    });

    // Socket cleanup
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        trackGoogleAnalyticsEvent('disconnect', 'socket', 'backend', 0);
      }
    };
  }, []);

  // Optimized updateReading function - COMPLETELY RESTORED WITH ALL LINES
  const updateReading = useCallback(({ meter_id, reading, timestamp, consumption_rate }) => {
    if (!meter_id) return;

    const meterData = getDynamicMeterData();
    const meterInfo = meterData[meter_id];
    
    if (!meterInfo) return;

    const currentReading = parseFloat(reading) || 0;
    const meterCost = meterInfo?.cost || 0;
    const totalCost = (currentReading * (meterCost / 100)).toFixed(2);
    
    const newReading = {
      ...meterInfo,
      reading: (reading || 0).toFixed(2),
      total: totalCost,
    };
    
    // Update historical data - ALL LINES RESTORED
    setHistoricalData(prev => ({
      ...prev,
      [meter_id]: [
        ...(prev[meter_id] || []),
        { 
          reading: reading || 0, 
          timestamp: timestamp || new Date().toISOString() 
        }
      ].slice(-10)
    }));

    // Update consumption rates
    if (consumption_rate) {
      setConsumptionRates(prev => ({
        ...prev,
        [meter_id]: consumption_rate
      }));
    }
    
    // Update readings
    setReadings(prev => ({
      ...prev,
      [meter_id]: newReading
    }));
    
    setLoading(false);
    
    // Track meter reading update
    trackGoogleAnalyticsEvent('update', 'meter_reading', meter_id, currentReading);
  }, []);

  // Setup socket listener
  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.on("newReading", updateReading);
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off("newReading", updateReading);
      }
    };
  }, [updateReading]);

  const handleCalculate = (e) => {
    e.preventDefault();
    const reading = parseFloat(userReading);
    if (!isNaN(reading)) {
      const meterData = getDynamicMeterData();
      const results = Object.entries(meterData || {}).map(([id, data]) => {
        const calculatedCost = (reading * ((data?.cost || 0) / 100)).toFixed(2);
        return {
          id,
          supplier: data?.supplier || 'Unknown Supplier',
          cost: calculatedCost,
          tariff: data?.tariff || 'Standard',
          contact: data?.contact || 'Contact not available',
          target: data?.target || '#',
          affiliateLink: data?.affiliateLink || '#',
          commissionRate: data?.commissionRate || 0,
          total: calculatedCost
        };
      });
      setCalculatedResults(results);
      
      // Track calculation event
      trackGoogleAnalyticsEvent('calculate', 'meter_reading', 'manual_calculation', reading);
    }
  };

  const handleCloseResults = () => {
    setCalculatedResults([]);
    setUserReading("");
    trackGoogleAnalyticsEvent('close', 'results', 'manual_calculation', 0);
  };

  const stopReading = () => {
    if (socketRef.current) {
      socketRef.current.emit("stopReading");
    }
    setIsReadingActive(false);
    trackGoogleAnalyticsEvent('stop', 'meter_reading', 'auto_reading', 0);
  };

  const startReading = () => {
    if (socketRef.current) {
      socketRef.current.emit("startReading");
    }
    setIsReadingActive(true);
    trackGoogleAnalyticsEvent('start', 'meter_reading', 'auto_reading', 1);
  };

  const handleAffiliateLinkClick = (supplier, meterId, e) => {
    e.stopPropagation();
    trackAffiliateClick(supplier, meterId);
  };

  // MODAL HANDLERS (RESTORED)
  const handleReadingChange = (index, value) => {
    const newReadings = [...meterReadings];
    newReadings[index].value = value;
    setMeterReadings(newReadings);
  };

  const handleDateChange = (index, date) => {
    const newReadings = [...meterReadings];
    newReadings[index].date = date;
    setMeterReadings(newReadings);
  };

  const handleAddReading = () => {
    setMeterReadings([...meterReadings, { value: "", date: new Date().toISOString().split('T')[0] }]);
    trackGoogleAnalyticsEvent('add', 'meter_reading', 'reading_field', meterReadings.length + 1);
  };

  const handleRemoveReading = (index) => {
    if (meterReadings.length > 1) {
      const newReadings = [...meterReadings];
      newReadings.splice(index, 1);
      setMeterReadings(newReadings);
      trackGoogleAnalyticsEvent('remove', 'meter_reading', 'reading_field', meterReadings.length - 1);
    }
  };

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setMeterReadings([{ value: "", date: new Date().toISOString().split('T')[0] }]);
    startReading();
    trackGoogleAnalyticsEvent('close', 'modal', 'meter_details', 0);
  }, []);

  const handleSendReading = useCallback(async () => {
    if (modalData && user) {
      setIsSending(true);
      try {
        const response = await fetch(CONFIG.GMAIL_APP_CONFIG.apiEndpoint, {
          method: "POST",
          redirect: "follow",
          headers: {
            "Content-Type": "text/plain;charset=utf-8",
          },
          body: JSON.stringify({
            userEmail: user.primaryEmailAddress?.emailAddress || "",
            meterData: {
              id: modalData.id,
              readings: meterReadings,
              supplier: modalData.supplier,
              tariff: modalData.tariff,
              cost: modalData.cost,
              total: meterReadings.reduce((sum, reading) => {
                const value = parseFloat(reading.value) || 0;
                return sum + (value * (modalData.cost / 100));
              }, 0).toFixed(2),
              accountNumber: modalData.accountNumber,
            },
          }),
        });

        const data = await response.json();

        if (data.success) {
          toast.success("Meter reading details sent!");
          setShowModal(false);
          setMeterReadings([{ value: "", date: new Date().toISOString().split('T')[0] }]);
          startReading();
          
          // Track successful submission
          trackGoogleAnalyticsEvent('send', 'meter_reading', 'email_submission', meterReadings.length);
        } else {
          throw new Error(data.error || "Failed to send email");
        }
      } catch (error) {
        console.error("Email error:", error);
        toast.error("Failed to send email: " + error.message);
        
        // Track error
        trackGoogleAnalyticsEvent('error', 'meter_reading', 'email_failed', 0);
      } finally {
        setIsSending(false);
      }
    }
  }, [modalData, user, meterReadings]);

  const handlePay = useCallback(() => {
    const total = meterReadings.reduce((sum, reading) => {
      const value = parseFloat(reading.value) || 0;
      return sum + (value * (modalData.cost / 100));
    }, 0).toFixed(2);
    console.log("Paying £" + total);
    trackGoogleAnalyticsEvent('pay', 'meter_reading', 'payment_attempt', parseFloat(total));
  }, [modalData, meterReadings]);

  const handleAccountNumberChange = useCallback((e) => {
    setModalData((prev) => ({
      ...prev,
      accountNumber: e.target.value,
    }));
  }, []);

  // HANDLE METER CLICK TO OPEN MODAL (RESTORED)
  const handleMeterSelect = useCallback((meterId, data, e) => {
    if (e.target.closest('.target-link')) {
      return;
    }
    
    stopReading();
    const meterSnapshot = {
      id: meterId,
      reading: data.reading,
      supplier: data.supplier,
      tariff: data.tariff,
      cost: data.cost,
      total: data.total,
      emailAddress: "",
      accountNumber: "",
      affiliateLink: data.affiliateLink,
      commissionRate: data.commissionRate
    };
    setModalData(meterSnapshot);
    setMeterReadings([{ value: data.reading || "", date: new Date().toISOString().split('T')[0] }]);
    setShowModal(true);
    
    // Track meter selection
    trackGoogleAnalyticsEvent('select', 'meter', meterId, 1);
  }, []);

  const handleResultClick = (result) => {
    stopReading();
    const currentReading = parseFloat(userReading) || 0;
    const totalCost = (currentReading * (result.cost / 100)).toFixed(2);
    
    const meterSnapshot = {
      id: result.id,
      reading: userReading || "0",
      supplier: result.supplier,
      tariff: result.tariff,
      cost: result.cost,
      total: totalCost,
      emailAddress: "",
      accountNumber: "",
      affiliateLink: result.affiliateLink,
      commissionRate: result.commissionRate
    };
    setModalData(meterSnapshot);
    setMeterReadings([{ value: userReading || "", date: new Date().toISOString().split('T')[0] }]);
    setShowModal(true);
    
    // Track result click
    trackGoogleAnalyticsEvent('select', 'calculation_result', result.supplier, currentReading);
  };

  // RENDER MODAL (RESTORED)
  const renderModal = () => {
    if (!modalData || !showModal) return null;

    return (
      <Modal
        readings={meterReadings}
        onReadingChange={handleReadingChange}
        onDateChange={handleDateChange}
        onAddReading={handleAddReading}
        onRemoveReading={handleRemoveReading}
        onClose={handleModalClose}
        onSend={handleSendReading}
        onPay={handlePay}
        isSending={isSending}
        meterInfo={modalData}
        onAccountNumberChange={handleAccountNumberChange}
      />
    );
  };

  // MeterRow component WITH CLICK HANDLER
  const MeterRow = memo(({ meterId, data }) => {
    const chartData = useMemo(() => ({
      labels: historicalData[meterId]?.map((_, i) => i) || [],
      datasets: [{
        label: 'Energy Usage (kWh)',
        data: historicalData[meterId]?.map(d => d.reading) || [],
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        fill: false
      }]
    }), [historicalData, meterId]);

    const currentReading = parseFloat(data?.reading) || 0;
    const meterCost = data?.cost || 0;
    const totalCost = (currentReading * (meterCost / 100)).toFixed(2);
    const consumptionRate = consumptionRates[meterId] || "Normal";

    return (
      <div className="meter-row">
        <div 
          className="card card-interactive"
          onClick={(e) => handleMeterSelect(meterId, data, e)}
        >
          <div className="card-header">
            <div className="card-title">
              <span>{data?.supplier || 'Unknown Supplier'}</span> {`Meter ${meterId}`}
              <span className="status-indicator">
                <span className={`ping ${isReadingActive ? "active" : "stopped"}`}></span>
                <span className={`dot ${isReadingActive ? "active" : "stopped"}`}></span>
              </span>
            </div>
          </div>
          <div className="card-content">
            <div className="reading-display">
              <span>{data?.reading || "0.00"}</span>
              <span className="unit">kWh</span>
              <p className="changeReading">Send your reading(s).</p>
            </div>
            <div className="other-display">
              <span className="unit2">Supplier:</span>
              <span>{data?.supplier || 'Unknown Supplier'}</span>
            </div>
            <div className="other-display">
              <span className="unit2">Tariff:</span>
              <span>{data?.tariff || 'Standard'}</span>
            </div>
            <div className="other-display">
              <span className="unit2">Cost per kWh:</span>
              <span>{meterCost}p</span>
            </div>
            <div className="other-display">
              <span className="unit2">Total Cost:</span>
              <span>£{totalCost}</span>
            </div>
            <div>Contact: {data?.contact || 'Contact not available'}</div>
            {data?.affiliateLink && (
              <div className="target-link" onClick={(e) => handleAffiliateLinkClick(data.supplier, meterId, e)}>
                <a href={data.affiliateLink} target="_blank" rel="noopener noreferrer">
                  SWITCH to {data.supplier} ! (Earn us {data.commissionRate * 100}%)
                </a>
              </div>
            )}
          </div>
        </div>
        <br />
        <div className="meter-graph">
          <div className="consumption-rate-label" style={{
            textAlign: 'center',
            padding: '5px',
            backgroundColor: consumptionRate.includes('High') ? '#ffebee' : 
                             consumptionRate.includes('Low') ? '#e8f5e8' : '#fff3e0',
            color: consumptionRate.includes('High') ? '#c62828' : 
                   consumptionRate.includes('Low') ? '#1b5e20' : '#ef6c00',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            borderRadius: '4px 4px 0 0'
          }}>
            Current Consumption: {consumptionRate}
          </div>
          <Line 
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              animation: {
                duration: 1000 // Keep animations but at reasonable speed
              },
              plugins: {
                legend: {
                  display: false
                }
              },
              scales: {
                y: {
                  beginAtZero: false
                }
              }
            }}
          />
        </div>
      </div>
    );
  });

  MeterRow.displayName = "MeterRow";

  const MeterGrid = memo(() => {
    const meterData = getDynamicMeterData();
    const meterIds = Object.keys(meterData);
    
    // Track meter grid view
    useEffect(() => {
      trackGoogleAnalyticsEvent('view', 'meter_grid', 'grid_display', meterIds.length);
    }, [meterIds.length]);
    
    return (
      <div className="meter-grid">
        {meterIds.map((meterId) => {
          const data = readings[meterId] || {
            ...meterData[meterId],
            reading: "0.00",
            total: "0.00"
          };
          
          return (
            <MeterRow 
              key={meterId}
              meterId={meterId}
              data={data}
            />
          );
        })}
      </div>
    );
  });

  MeterGrid.displayName = "MeterGrid";

  return (
    <div className="app-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <nav className="navbar">
        <div className="navbar-brand">
          <h2 style={{ fontWeight: "bold", color: "green" }}><a href="/">SPYDER</a></h2>
        </div>
        <div className="navbar-auth">
          <SignedOut>
            <SignInButton mode="modal" />
          </SignedOut>
          <SignedIn>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </div>
       {/* <div>
          <Link to="/newsletter" className="crumbtrail"><small>Newsletter</small></Link> 
        </div>*/}
      </nav>

      <div className="main-content-container">
        <div className="content-area">
          <div className="container">
            <h3 className="title">Price Comparison Smart Energy Meter Reader</h3>
            <p className="app-description">
             An AI NetZero initiative, <strong>SPYDER</strong>  is a  Digital Twin Smart Energy Meter Reader price comparison platform, designed to empower businesses and consumers with
             actionable insights for reducing carbon emissions and adopting sustainable energy solutions. 
             The Reader also serves as a forecasting system, a settlement tool and 
             an artificial intelligence run-time quoting engine for finding the best energy tariffs at most competitive prices, saving you money!  
             Please login for optimum interactive experience to make informed decisions.
            </p>
            
            <SignedOut>
              <p className="auth-prompt">
                Start comparing now and make smarter choices for your electricity
                usage. Please &nbsp;
                <SignInButton mode="modal" className="login-button">
                  Sign in &nbsp;
                </SignInButton>
                &nbsp;&nbsp; and select a Smart Meter!
              </p>
            </SignedOut>
            
            <SignedIn>
              <div className="reading-form-container">
                <form onSubmit={handleCalculate}>
                  <input
                    type="number"
                    value={userReading}
                    onChange={(e) => setUserReading(e.target.value)}
                    className="reading-input"
                    placeholder="Please Enter your reading"
                    step="0.01"
                    min="0"
                    required
                  />
                  <button type="submit" className="calculate-button">
                    Calculate
                  </button>
                </form>

                {calculatedResults.length > 0 && (
                  <div className="results-container">
                    <table className="cost-table">
                      <thead>
                        <tr>
                          <th>Energy Company</th>
                          <th>Total Cost (£)</th>
                          <th>Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calculatedResults.map((result, index) => (
                          <tr key={index} onClick={() => handleResultClick(result)} className="result-row">
                            <td>
                              <span className="switch-tariff-supplier">
                                {result.supplier} 
                                <a href={result.affiliateLink} target="_blank" rel="noopener noreferrer">
                                  <strong>(SWITCH!)</strong>
                                </a>
                              </span>
                            </td>
                            <td>£{result.cost}</td>
                            <td>{result.commissionRate * 100}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button 
                      onClick={handleCloseResults} 
                      className="close-results-button"
                    >
                      Close Results
                    </button>
                  </div>
                )}
              </div>
                           
              <MeterGrid />

              {/* RENDER MODAL */}
              {renderModal()}



                  {/* Stop/Start buttons */}
              <div className="button-container">
                {isReadingActive ? (
                  <button onClick={() => stopReading()} className="stop-button">
                    Stop
                  </button>
                ) : (
                  <button onClick={() => startReading()} className="start-button">
                    Start
                  </button>
                )}
              </div>


              <div className="askJIM">
                <AskJim 
                  readings={readings}
                  consumptionRates={consumptionRates}
                />
              </div>

            </SignedIn>
          </div>

          <div className="ad-container">
            <div className="ad-column">
              <div className="ad-label">
                <div className="responsive-iframe-container" style={{ height: '457px' }}>
                  <iframe
                    src="https://www.youtube.com/embed/O7ACNMj8NW0"
                    title="Evolution of Tesla"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    style={{ height: '100%', width: '100%' }}
                    onLoad={() => trackGoogleAnalyticsEvent('load', 'iframe', 'tesla_video', 1)}
                  ></iframe>
                </div>
              </div>
            </div>

            <div className="ad-column">
              <div className="ad-label">
                <div className="media-grid">
                  <div className="media-container image" style={{ height: '300px' }}>
                    <span>100% Organic</span>
                    <iframe
                      src={hellofresh}
                      title="100% Organic"
                      frameBorder="0"
                      allowFullScreen
                      style={{ height: '100%', width: '100%' }}
                      onLoad={() => trackGoogleAnalyticsEvent('load', 'iframe', 'organic_image', 1)}
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="brand-story-container">
            <div className="brand-story-column">
              <div className="immersive-brand-ad">
                <div className="brand-narrative">
                  <div className="narrative-content">
                    <div className="narrative-text">
                      <h3>Our Journey to Sustainability</h3>
                      <p>
                        Founded in 2010, GreenPower Solutions began with a simple mission: to make renewable energy 
                        accessible to everyone.
                      </p>
                      <div className="brand-stats">
                        <div className="stat-item">
                          <span className="stat-number">1M+</span>
                          <span className="stat-label">Homes Powered</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-number">85%</span>
                          <span className="stat-label">Carbon Reduction</span>
                        </div>
                      </div>

                      {/* UPDATED: Added configurable Sustainability Journey component */}
                      <SustainabilityJourney />

                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="brand-story-column">
              <div className="product-showcase">
                <h3>Innovation That Powers Life</h3>
                <div className="product-features">
                  <div className="feature">
                    <div className="feature-video-container">
                      <img 
                        src={heatpump}
                        alt="Heat Pump Technology"
                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                        onLoad={() => trackGoogleAnalyticsEvent('load', 'image', 'heat_pump', 1)}
                      />
                    </div>
                    <h4>Next-Gen Heat Pumps</h4>
                    <p>Energy efficient heating solutions for modern homes</p>
                  </div>
                  <div className="feature">
                    <div className="feature-video-container">
                      <img 
                        src={EDFSmartMeter}
                        alt="Smart Meter Technology"
                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                        onLoad={() => trackGoogleAnalyticsEvent('load', 'image', 'smart_meter', 1)}
                      />
                    </div>
                    <h4>Smart Meter Systems</h4>
                    <p>Real-time energy monitoring and optimization</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="brand-cta">
            <h3>Join the Energy Revolution</h3>
            <p>Get a personalized quote and see how much you could save</p>
            <div className="cta-buttons">
              <button 
                className="cta-primary"
                onClick={() => trackGoogleAnalyticsEvent('click', 'cta', 'uptime_button', 1)}
              >
                How We Achieved 100% Uptime!
              </button>
              <button 
                className="cta-secondary"
                onClick={() => trackGoogleAnalyticsEvent('click', 'cta', 'story_button', 1)}
              >
                Watch Our Story
              </button>
            </div>

           <div> <p><i>Powered by <strong><span className="poweredBy">Digital Data & Engineering.</span></strong></i></p></div>

            {/* Removed trust badges from here as they are now in AskJim component */}
          </div>
        </div>    
      </div>
    </div>
  );
}

export default function EnergyMeter() {
  return (
    <PayPalScriptProvider options={{ 
      "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || "YOUR_PAYPAL_CLIENT_ID",
      components: "buttons",
      currency: "GBP"
    }}>
      <App />
    </PayPalScriptProvider>
  );
}
